import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { listRecentOrders, paymentMethodLabelWithIyzico, type OrderListRecord } from '../../services/orders';
import { useAuth } from '../../contexts/AuthContext';

type StatusFilter = 'all' | OrderListRecord['status'];
type PaymentFilter = 'all' | OrderListRecord['paymentMethod'];
type RestaurantFilter = 'all' | string;

interface FilterState {
  search: string;
  status: StatusFilter;
  payment: PaymentFilter;
  restaurant: RestaurantFilter;
}

const STATUS_LABELS: Record<OrderListRecord['status'], string> = {
  pending: 'Onay bekliyor',
  confirmed: 'Hazırlanacak',
  preparing: 'Hazırlanıyor',
  on_the_way: 'Yolda',
  delivered: 'Teslim edildi',
  cancelled: 'İptal edildi',
};

const PAYMENT_STATUS_LABELS: Record<OrderListRecord['paymentStatus'], string> = {
  pending: 'Ödeme bekleniyor',
  completed: 'Ödeme alındı',
  failed: 'Başarısız',
  refunded: 'İade edildi',
  cancelled: 'İptal edildi',
};

const STATUS_CHIP_MAP: Partial<Record<OrderListRecord['status'], string>> = {
  delivered: 'success',
  cancelled: 'danger',
  pending: 'warning',
  confirmed: 'warning',
  preparing: 'warning',
};

const PAYMENT_CHIP_MAP: Partial<Record<OrderListRecord['paymentStatus'], string>> = {
  completed: 'success',
  failed: 'danger',
  refunded: 'neutral',
  pending: 'warning',
  cancelled: 'danger',
};

const defaultFilters: FilterState = {
  search: '',
  status: 'all',
  payment: 'all',
  restaurant: 'all',
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 2 }).format(value);

export default function OrdersOverview() {
  const { user } = useAuth();
  const allowedRestaurants = useMemo(() => {
    const meta = (user?.user_metadata ?? {}) as Record<string, unknown>;
    const raw = Array.isArray(meta.restaurant_slugs)
      ? meta.restaurant_slugs
      : Array.isArray(meta.restaurants)
        ? meta.restaurants
        : [];
    return raw.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
  }, [user]);

  const [filters, setFilters] = useState<FilterState>(defaultFilters);

  const {
    data: orders = [],
    isLoading,
    isFetching,
    error,
  } = useQuery({
    queryKey: ['operations', 'orders', { limit: 200 }],
    queryFn: () => listRecentOrders({ limit: 200 }),
  });

  useEffect(() => {
    if (allowedRestaurants.length === 1) {
      setFilters((prev) =>
        prev.restaurant === allowedRestaurants[0]
          ? prev
          : { ...prev, restaurant: allowedRestaurants[0] },
      );
      return;
    }
    if (allowedRestaurants.length > 1) {
      setFilters((prev) =>
        prev.restaurant !== 'all' && !allowedRestaurants.includes(prev.restaurant)
          ? { ...prev, restaurant: 'all' }
          : prev,
      );
    }
  }, [allowedRestaurants]);

  const restaurantOptions = useMemo(() => {
    const unique = new Map<string, string>();
    orders.forEach((order) => {
      const slug = order.restaurant.slug ?? order.restaurant.name ?? 'unassigned';
      const label = order.restaurant.name ?? 'Restoran seçilmedi';
      if (!unique.has(slug)) {
        unique.set(slug, label);
      }
    });
    let options = Array.from(unique.entries()).map(([slug, name]) => ({ value: slug, label: name }));
    if (allowedRestaurants.length > 0) {
      options = options.filter((option) => allowedRestaurants.includes(option.value));
    }
    return options;
  }, [orders, allowedRestaurants]);

  const filteredOrders = useMemo(() => {
    const search = filters.search.trim().toLowerCase();
    return orders.filter((order) => {
      if (allowedRestaurants.length > 0) {
        const slug = order.restaurant.slug ?? order.restaurant.name ?? '';
        if (!allowedRestaurants.includes(slug)) {
          return false;
        }
      }
      if (filters.status !== 'all' && order.status !== filters.status) {
        return false;
      }
      if (filters.payment !== 'all' && order.paymentMethod !== filters.payment) {
        return false;
      }
      if (filters.restaurant !== 'all') {
        const slug = order.restaurant.slug ?? order.restaurant.name ?? 'unassigned';
        if (slug !== filters.restaurant) {
          return false;
        }
      }
      if (search.length > 0) {
        const haystack = [
          order.id,
          order.customer.name,
          order.customer.email,
          order.customer.phone,
          order.restaurant.name,
          order.itemSummary,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(search)) {
          return false;
        }
      }
      return true;
    });
  }, [orders, filters, allowedRestaurants]);

  const deliveredCount = orders.filter((order) => order.status === 'delivered').length;
  const cancelledCount = orders.filter((order) => order.status === 'cancelled').length;
  const activeCount = orders.filter((order) => order.status !== 'delivered' && order.status !== 'cancelled').length;

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <p className="card-title">Son siparişler</p>
          <p className="card-subtitle">
            {isLoading || isFetching
              ? 'Siparişler güncelleniyor...'
              : `${filteredOrders.length} sipariş listeleniyor · ${deliveredCount} teslim edildi · ${activeCount} aktif · ${cancelledCount} iptal`}
          </p>
        </div>
      </div>

      <div className="form-row">
        <label className="form-field" aria-label="Sipariş ara">
          <span>Ara</span>
          <input
            type="search"
            placeholder="Sipariş no, müşteri, restoran..."
            value={filters.search}
            onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
          />
        </label>
        <label className="form-field">
          <span>Durum</span>
          <select
            value={filters.status}
            onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value as StatusFilter }))}
          >
            <option value="all">Tüm durumlar</option>
            {Object.entries(STATUS_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="form-field">
          <span>Ödeme</span>
          <select
            value={filters.payment}
            onChange={(event) => setFilters((prev) => ({ ...prev, payment: event.target.value as PaymentFilter }))}
          >
            <option value="all">Tüm yöntemler</option>
            <option value="credit_card">Kart</option>
            <option value="online_card">Online ödeme (iyzico)</option>
            <option value="cash">Nakit ödeme</option>
            <option value="meal_card">Yemek kartı</option>
            <option value="yemeksepeti">YS Vale</option>
          </select>
        </label>
        <label className="form-field">
          <span>Restoran</span>
          <select
            value={filters.restaurant}
            onChange={(event) =>
              setFilters((prev) => ({ ...prev, restaurant: event.target.value as RestaurantFilter }))
            }
          >
            {allowedRestaurants.length > 1 || allowedRestaurants.length === 0 ? (
              <option value="all">Hepsi</option>
            ) : null}
            {restaurantOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error ? (
        <div className="empty-state">
          <strong>Siparişler yüklenemedi</strong>
          <p>{error instanceof Error ? error.message : 'Sipariş verisine erişilemedi.'}</p>
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="empty-state">
          <strong>Filtreye uygun sipariş bulunamadı</strong>
          <p>Filtreyi değiştirerek yeniden deneyin.</p>
        </div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Sipariş</th>
              <th>Müşteri</th>
              <th>Ödeme</th>
              <th>Teslimat</th>
              <th>Restoran</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.map((order) => (
              <tr key={order.id}>
                <td>
                  <strong>{order.id.slice(0, 8).toUpperCase()}</strong>
                  <p className="helper-text">
                    {new Date(order.createdAt).toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul' })}
                  </p>
                  <p className="helper-text">{order.itemSummary}</p>
                  {order.notes ? <p className="helper-text">Not: {order.notes}</p> : null}
                </td>
                <td>
                  <div>{order.customer.name ?? 'İsimsiz müşteri'}</div>
                  <p className="helper-text">
                    {[order.customer.phone, order.customer.email].filter(Boolean).join(' · ') || 'İletişim yok'}
                  </p>
                </td>
                <td>
                  <div>{paymentMethodLabelWithIyzico(order.paymentMethod, (order as any).iyzicoPaymentId)}</div>
                  <div className="helper-text">{formatCurrency(order.totalAmount)}</div>
                  <span className={`chip ${PAYMENT_CHIP_MAP[order.paymentStatus] ?? 'neutral'}`}>
                    {PAYMENT_STATUS_LABELS[order.paymentStatus]}
                  </span>
                </td>
                <td>
                  <span className={`chip ${STATUS_CHIP_MAP[order.status] ?? 'neutral'}`}>
                    {STATUS_LABELS[order.status]}
                  </span>
                  {order.driver.name ? (
                    <p className="helper-text">
                      {order.driver.name}
                      {order.driver.phone ? ` · ${order.driver.phone}` : ''}
                    </p>
                  ) : null}
                  {order.driver.deliveredAt ? (
                    <p className="helper-text">
                      Teslim:{' '}
                      {new Date(order.driver.deliveredAt).toLocaleTimeString('tr-TR', {
                        hour: '2-digit',
                        minute: '2-digit',
                        timeZone: 'Europe/Istanbul',
                      })}
                    </p>
                  ) : order.driver.status ? (
                    <p className="helper-text">Durum: {order.driver.status}</p>
                  ) : null}
                </td>
                <td>
                  <div>{order.restaurant.name ?? 'Atanmamış'}</div>
                  <p className="helper-text">{order.addressSummary ?? 'Adres bilgisi yok'}</p>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
